import React from 'react';
import { Pencil, Trash2, UserCheck, UserX, Printer, MapPin, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { DualScrollTable } from '../../DualScrollTable';
import { generateSingleMapLink } from '../../MapView';

export const ClientTable = ({
    clients, clientCategories, selectedClients, handleSelectAll, handleToggleSelectClient,
    handleEdit, handleToggleStatus, handleDeleteClick, handlePrintClientCard
}) => {
    const getCategoryName = (categoryId) => {
        const cat = clientCategories.find(c => c.id === categoryId);
        return cat ? cat.name : categoryId || 'Autre';
    };

    if (clients.length === 0) {
        return (
            <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                <p className="text-muted-foreground">Aucun client trouve</p>
            </div>
        );
    }

    return (
        <DualScrollTable>
            <Table>
                <TableHeader>
                    <TableRow className="border-jungle/10">
                        <TableHead className="w-10">
                            <Checkbox checked={selectedClients.length === clients.length && clients.length > 0}
                                onCheckedChange={handleSelectAll} data-testid="select-all-clients" />
                        </TableHead>
                        <TableHead className="text-jungle/70">Code</TableHead>
                        <TableHead className="text-jungle/70">Entreprise</TableHead>
                        <TableHead className="text-jungle/70">Categorie</TableHead>
                        <TableHead className="text-jungle/70">Contact</TableHead>
                        <TableHead className="text-jungle/70">Email</TableHead>
                        <TableHead className="text-jungle/70">Telephone</TableHead>
                        <TableHead className="text-jungle/70">Statut</TableHead>
                        <TableHead className="text-jungle/70 text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {clients.map((client) => (
                        <TableRow key={client.id} className="border-jungle/10" data-testid={`client-row-${client.id}`}>
                            <TableCell>
                                <Checkbox checked={selectedClients.includes(client.id)}
                                    onCheckedChange={() => handleToggleSelectClient(client.id)}
                                    data-testid={`select-client-${client.id}`} />
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className="font-mono text-xs rounded-full border-jungle/30">{client.client_code || '-'}</Badge>
                            </TableCell>
                            <TableCell>
                                <div>
                                    <p className="font-medium text-jungle">{client.company_name}</p>
                                    {client.siret && <p className="text-xs text-muted-foreground">SIRET: {client.siret}</p>}
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge className="bg-leaf/10 text-leaf rounded-full">{getCategoryName(client.client_category)}</Badge>
                            </TableCell>
                            <TableCell className="text-jungle">{client.contact_name || '-'}</TableCell>
                            <TableCell className="text-muted-foreground">{client.email || '-'}</TableCell>
                            <TableCell className="text-muted-foreground">{client.phone || '-'}</TableCell>
                            <TableCell>
                                <Badge variant={client.is_active ? 'default' : 'destructive'} className="rounded-full">
                                    {client.is_active ? 'Actif' : 'Inactif'}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <div className="flex justify-end gap-1">
                                    {client.address && (
                                        <a href={generateSingleMapLink(client.address)} target="_blank" rel="noopener noreferrer">
                                            <Button variant="ghost" size="sm" className="text-jungle" title="Maps"><MapPin className="h-4 w-4" /></Button>
                                        </a>
                                    )}
                                    <Button variant="ghost" size="sm" onClick={() => handlePrintClientCard(client)}
                                        className="hover:bg-sage/10" data-testid={`print-client-${client.id}`} title="Imprimer">
                                        <Printer className="h-4 w-4 text-sage" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(client)} className="hover:bg-leaf/10" data-testid={`edit-client-${client.id}`}>
                                        <Pencil className="h-4 w-4 text-leaf" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(client.id)}
                                        className={client.is_active ? 'hover:bg-destructive/10' : 'hover:bg-leaf/10'} data-testid={`toggle-client-${client.id}`}>
                                        {client.is_active ? <UserX className="h-4 w-4 text-destructive" /> : <UserCheck className="h-4 w-4 text-leaf" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => handleDeleteClick(client)} data-testid={`delete-client-${client.id}`}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </DualScrollTable>
    );
};
